import { create } from 'zustand';
import { freezeUiFlags, type ServerUiFlags } from '@/lib/flags';
import { devtools } from 'zustand/middleware';
import type { Role, MembershipSummary } from '@revcall/shared';
import { apiClient } from '@/api/client';

/**
 * authStore — source of truth for the current user and active Membership.
 *
 * Hydrated once after login / page reload via GET /api/auth/me; updated
 * atomically by switchOrganisation(). Mirrors the JWT v2 claims defined
 * in @revcall/shared/schemas/identity/jwt-claims.schema.ts (ADR-021).
 *
 * The active Membership is nullable because a super admin can act
 * cross-tenant without being scoped to any Organisation.
 *
 * After a switchOrganisation() call, callers should invalidate the
 * React Query cache (qc.clear()) — different Organisation = different
 * data scope. The useAuth hook wraps that orchestration.
 */

export interface ActiveMembership {
  membershipId: string;
  organisationId: string;
  organisationName: string;
  role: Role;
  /** ADR-027 — non-empty only for viewer with per-Customer scope. */
  customerIds: string[];
}

/** SU-1 / ENG-10: a super admin viewing this member read-only (« ouvrir en tant que »). */
export interface Impersonation {
  actorId: string;
  actorEmail: string;
  subjectOrganisationId: string;
  expiresAt: string;
}

export interface AuthUser {
  id: string;
  email: string;
  isSuperAdmin: boolean;
}

interface AuthState {
  user: AuthUser | null;
  activeMembership: ActiveMembership | null;
  availableMemberships: MembershipSummary[];
  /** Set while a super admin views this session's member read-only (ENG-10). */
  impersonation: Impersonation | null;
  isLoading: boolean;
  error: string | null;
  /**
   * The session could not be checked (429, 5xx, network), which is not the
   * same as being logged out. Guards show a retry screen instead of sending
   * the user to /Login (RUI-13).
   */
  unavailable: boolean;

  /** Fetch /api/auth/me and populate the store. Called at app boot. */
  refresh(): Promise<void>;
  /** Switch active Organisation. Returns the new active Membership (or null). */
  switchOrganisation(organisationId: string): Promise<ActiveMembership | null>;
  /** Clear local state (e.g. on logout). Does NOT call the backend. */
  reset(): void;
  /** The session check failed for a reason other than authentication. */
  markUnavailable(): void;
}

interface MeResponse {
  id: string;
  /** Resolved server-side; the client freezes them once per page load (lib/flags). */
  ui_flags?: ServerUiFlags;
  is_super_admin?: boolean;
  auth?: {
    userId: string;
    email: string;
    isSuperAdmin: boolean;
    activeMembership: ActiveMembership | null;
    impersonation?: Impersonation;
  };
  caller_email?: string;
}

interface MembershipsResponse {
  memberships: MembershipSummary[];
}

interface SwitchResponse {
  auth: {
    userId: string;
    email: string;
    isSuperAdmin: boolean;
    activeMembership: ActiveMembership | null;
  };
}

/**
 * 401/403 mean "not logged in". Anything else (429 rate limit, 5xx, a
 * network error without a status) says nothing about the session.
 */
export function isTransientAuthFailure(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status !== 'number' || (status !== 401 && status !== 403);
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      activeMembership: null,
      availableMemberships: [],
      impersonation: null,
      // 2026-09-15 — demarre a `true`, PAS a `false`. Un garde de route lit
      // `isLoading` pour distinguer « je n'ai pas encore hydrate » de « j'ai
      // hydrate, il n'y a personne ». A `false`, les deux etats etaient
      // indiscernables : RequireRole voyait user=null/isLoading=false au tout
      // premier rendu — avant meme que AuthBootstrap ait lance refresh() — et
      // redirigeait vers /Login, qui renvoyait aussitot le super-admin sur
      // /AdminOrganisations par window.location.href. Boucle de redirection
      // infinie, en rechargements complets. Cf. BUGS-OUVERTS.md (AUTH-1).
      // reset() repose `false`, donc un utilisateur reellement deconnecte
      // sort du spinner et tombe bien sur /Login.
      isLoading: true,
      error: null,
      unavailable: false,

      async refresh() {
        set({ isLoading: true, error: null, unavailable: false });
        try {
          const [me, memberships] = await Promise.all([
            apiClient.get<MeResponse>('/auth/me'),
            apiClient.get<MembershipsResponse>('/auth/memberships').catch(() => ({
              memberships: [] as MembershipSummary[],
            })),
          ]);
          freezeUiFlags(me.ui_flags);
          if (!me.auth) {
            // Legacy viewer response without .auth (pre-v2 caller not yet migrated)
            set({ user: null, activeMembership: null, isLoading: false });
            return;
          }
          set({
            user: {
              id: me.auth.userId,
              email: me.auth.email,
              isSuperAdmin: me.auth.isSuperAdmin,
            },
            activeMembership: me.auth.activeMembership,
            availableMemberships: memberships.memberships,
            impersonation: me.auth.impersonation ?? null,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          set({
            user: null,
            activeMembership: null,
            availableMemberships: [],
            isLoading: false,
            // i18n-exempt: internal state; RequireRole only tests it and redirects
            error: err instanceof Error ? err.message : 'Failed to load session',
            unavailable: isTransientAuthFailure(err),
          });
        }
      },

      async switchOrganisation(organisationId) {
        const res = await apiClient.post<SwitchResponse>('/auth/switch-organisation', {
          organisation_id: organisationId,
        });
        set({
          user: {
            id: res.auth.userId,
            email: res.auth.email,
            isSuperAdmin: res.auth.isSuperAdmin,
          },
          activeMembership: res.auth.activeMembership,
        });
        return res.auth.activeMembership;
      },

      reset() {
        set({
          user: null,
          activeMembership: null,
          availableMemberships: [],
          impersonation: null,
          isLoading: false,
          error: null,
          unavailable: false,
        });
      },

      markUnavailable() {
        set({ user: null, activeMembership: null, isLoading: false, unavailable: true });
      },
    }),
    { name: 'authStore' },
  ),
);
